import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { NotifyService } from 'src/app/service/notify.service';
import { FileHandle } from '../../../../../drag-drop-files.directive';
import { PanoptycAdminService } from '../../../../../service/panoptyc-admin.service';
import { SignalrTransactionUploadsService } from '../../../../../service/signalr-transaction-uploads.service';
import { copyToClipboard } from '../../../../../public/util/navigator.utils';
import { TransactionUpload } from 'src/app/types/transaction';

@Component({
  selector: 'app-upload-transactions',
  templateUrl: './upload-transactions.component.html',
  styleUrl: './upload-transactions.component.scss',
  standalone: false,
})
export class AdminUploadTransactionsComponent implements OnInit {
  private adminService = inject(PanoptycAdminService);
  public signalrService = inject(SignalrTransactionUploadsService);
  notifyService = inject(NotifyService);
  destroyRef = inject(DestroyRef);

  transactionFiles: FileHandle[] = [];
  public isUpdating: boolean;
  public uploads = signal<TransactionUpload[]>([]);
  public selectedUploader = signal<string | null>(null);

  fileTypeBgColor = {
    csv: 'bg-gray-300',
    xlsx: 'bg-gray-300',
    excel: 'bg-gray-300',
    undefined: 'bg-gray-500',
  };

  statusBgColor = {
    Uploaded: 'bg-blue-700',
    Analyzing: 'bg-yellow-400',
    ReadyToImport: 'bg-blue-700',
    Importing: 'bg-yellow-400',
    Failed: 'bg-red-700',
    Done: 'bg-green-700',
    UnmappedBranchLocation: 'bg-orange-700',
    NeedsUserInput: 'bg-purple-700',
  };

  rowBgColor = {
    NeedsUserInput: 'bg-yellow-100',
  };

  // fields related to row selection
  selectedUploadIds: Set<string> = new Set();
  lastSelectedIndex: number = -1;
  isShiftPressed: boolean = false;
  isDeleting = signal(false);

  // Not a computed(): selectedUploadIds is a plain mutated Set, not a signal, so this needs to
  get isAllSelected(): boolean {
    const filteredUploads = this.filteredUploads();
    return filteredUploads.length > 0 && this.selectedUploadIds.size === filteredUploads.length;
  }

  distinctUploaders = computed(() => {
    const uploadedBy = this.uploads()
      .map((upload) => upload.uploaded_by)
      .filter((uploadedBy): uploadedBy is string => !!uploadedBy);
    return Array.from(new Set(uploadedBy)).sort();
  });

  filteredUploads = computed(() => {
    const uploads = this.uploads();
    const selectedUploader = this.selectedUploader();
    if (!selectedUploader) return uploads;
    return uploads.filter((upload) => upload.uploaded_by === selectedUploader);
  });

  renderTransactionStatuses = (transaction_statuses: { status: string; count: number }[] | undefined) => {
    return transaction_statuses?.filter((s) => s.status).map((s) => `${s.status}:${s.count}\n`) ?? '';
  };

  ngOnInit(): void {
    this.isUpdating = true;
    this.signalrService.transactionUploadsConnected
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((isConnected) => {
          if (!isConnected) return;
          this.signalrService.getCurrentTransactionUploadsStatus();
        }),
        switchMap((isConnected) => {
          if (isConnected) {
            return this.signalrService.transactionUploads;
          }
          return [];
        }),
      )
      .subscribe({
        next: (data) => {
          if (data) {
            this.uploads.set(data);
          }
        },
      });

    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event.key === 'Shift'),
      )
      .subscribe(() => {
        this.isShiftPressed = true;
      });

    fromEvent<KeyboardEvent>(document, 'keyup')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event.key === 'Shift'),
      )
      .subscribe(() => {
        this.isShiftPressed = false;
      });
  }

  filesDropped($event: FileHandle[]) {
    this.transactionFiles = $event;
    this.uploadAndProcessTransactions();
  }

  onCsvPicked($event: Event) {
    this.transactionFiles = [];
    const files = ($event.target as HTMLInputElement).files;
    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      this.transactionFiles.push({ file: files[fileIndex], url: files[fileIndex].webkitRelativePath });
    }
    this.uploadAndProcessTransactions();
  }

  uploadAndProcessTransactions() {
    if (this.transactionFiles.length === 0) {
      return;
    }
    this.isUpdating = true;
    this.adminService.uploadTransactionsFile(this.transactionFiles.map((t) => t.file)).subscribe(() => {
      this.isUpdating = false;
    });
  }

  formatDateAgo(value: any) {
    if (value) {
      const seconds = Math.floor((+new Date() - +new Date(value)) / 1000);
      if (seconds < 29) {
        // less than 30 seconds ago will show as 'Just now'
        return 'Just now';
      }
      const intervals = {
        year: 31536000,
        month: 2592000,
        week: 604800,
        day: 86400,
        hour: 3600,
        minute: 60,
        second: 1,
      };
      let counter;
      for (const i in intervals) {
        counter = Math.floor(seconds / intervals[i]);
        if (counter > 0) {
          if (counter === 1) {
            return counter + ' ' + i + ' ago'; // singular (1 day ago)
          } else {
            return counter + ' ' + i + 's ago'; // plural (2 days ago)
          }
        }
      }
    }
    return value;
  }

  onUploadBranchAliasChange(event: Event, upload: TransactionUpload) {
    const value = (event.target as HTMLSelectElement).value;
    upload.status_details_parsed.branch_alias = value;
    this.adminService.setUploadBranchAlias(upload.id, value).subscribe(() => {});
  }

  canBeRetried(upload: TransactionUpload) {
    const nonRetryableStatuses = ['Done', 'NeedsUserInput'];
    return !nonRetryableStatuses.includes(upload.status);
  }

  retryUpload(upload: TransactionUpload) {
    upload.status = 'Importing';
    this.adminService.retryUpload(upload.id).subscribe(() => {});
  }

  copyUploadId($event: Event, uploadId: string) {
    copyToClipboard(uploadId);

    this.notifyService.notify('Upload ID copied');
  }

  toggleSelectAll(event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.selectedUploadIds.clear();
    if (isChecked) {
      this.filteredUploads().forEach((upload) => this.selectedUploadIds.add(upload.id));
    }
  }

  isSelected(uploadId: string): boolean {
    return this.selectedUploadIds.has(uploadId);
  }

  onCheckboxChange(event: Event, uploadId: string, index: number) {
    const isChecked = (event.target as HTMLInputElement).checked;
    const toggleSelection = (id: string) => (isChecked ? this.selectedUploadIds.add(id) : this.selectedUploadIds.delete(id));

    if (this.isShiftPressed && this.lastSelectedIndex !== -1) {
      const start = Math.min(this.lastSelectedIndex, index);
      const end = Math.max(this.lastSelectedIndex, index);
      const filteredUploads = this.filteredUploads();

      for (let i = start; i <= end; i++) {
        toggleSelection(filteredUploads[i].id);
      }

      this.lastSelectedIndex = -1;
    } else {
      toggleSelection(uploadId);
    }

    this.lastSelectedIndex = index;
  }

  onUploaderFilterChange(): void {
    this.lastSelectedIndex = -1;
  }

  hasSelectedRows(): boolean {
    return this.filteredUploads().some((upload) => this.isSelected(upload.id));
  }

  deleteSelectedUploads(): void {
    const uploadIdsToDelete = this.filteredUploads()
      .filter((upload) => this.isSelected(upload.id))
      .map((upload) => upload.id);
    if (uploadIdsToDelete.length === 0) return;
    this.isDeleting.set(true);
    this.notifyService.notify(`Deleting ${uploadIdsToDelete.length} transactions...`);
    this.adminService
      .deleteTransactions(uploadIdsToDelete)
      .pipe(
        finalize(() => {
          this.isDeleting.set(false);
        }),
      )
      .subscribe(
        () => {
          this.notifyService.notify(`Deleted ${uploadIdsToDelete.length} transactions`);
        },
        (err) => {
          this.notifyService.notify(`Failed to delete transactions: ${err.message}`);
        },
      );
  }
}
